/**
 * NavigatorX Voice Cloning Dashboard
 * 
 * Enterprise-grade admin dashboard for voice cloning feature:
 * - Voice clones overview with statistics
 * - Filter by status, relationship, payment
 * - Search by user, voice name
 * - Retry failed processing
 * - View voice details
 * - Delete voices with cleanup
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Mic,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
  RefreshCw,
  Search,
  Filter,
  Play,
  Trash2,
  Eye,
  ChevronDown,
  ChevronRight,
  DollarSign,
  XCircle,
  RotateCcw,
  Volume2,
  Star,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../../../utils/firebase/client';
import VoiceDetailModal from './VoiceDetailModal';
import {
  getVoiceClones,
  getCreatedVoices,
  getMediaClonedVoices,
  getVoiceStats,
  retryVoiceProcessing,
  deleteVoice,
  deleteCreatedVoice,
  deleteMediaClonedVoice,
  unpublishCreatedVoice,
  bulkRetryFailedVoices,
  VOICE_RECORDING_STATUS,
  VOICE_RELATIONSHIPS,
  type VoiceClone,
  type CreatedVoiceProfile,
  type MediaClonedVoiceProfile,
  type VoiceStats,
  type VoiceRecordingStatus
} from '../../../utils/services/navigatorXVoiceService';

interface VoiceCloningDashboardProps {
  embedded?: boolean;
}

export default function VoiceCloningDashboard({ embedded = false }: VoiceCloningDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [voices, setVoices] = useState<VoiceClone[]>([]);
  const [createdVoices, setCreatedVoices] = useState<CreatedVoiceProfile[]>([]);
  const [mediaClonedVoices, setMediaClonedVoices] = useState<MediaClonedVoiceProfile[]>([]);
  const [stats, setStats] = useState<VoiceStats | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [relationshipFilter, setRelationshipFilter] = useState<string>('all');
  const [paidFilter, setPaidFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // UI State
  const [selectedVoice, setSelectedVoice] = useState<VoiceClone | null>(null);
  const [expandedVoice, setExpandedVoice] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [bulkRetrying, setBulkRetrying] = useState(false);

  const adminId = auth.currentUser?.uid || 'unknown';

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [voicesData, statsData, createdVoicesData, mediaVoicesData] = await Promise.all([
        getVoiceClones({
          status: statusFilter !== 'all' ? statusFilter as VoiceRecordingStatus : undefined,
          relationship: relationshipFilter !== 'all' ? relationshipFilter : undefined,
          isPaid: paidFilter === 'all' ? undefined : paidFilter === 'paid',
          searchQuery: searchQuery || undefined,
          limitCount: 500
        }),
        getVoiceStats(),
        getCreatedVoices({
          isPaid: paidFilter === 'all' ? undefined : paidFilter === 'paid',
          searchQuery: searchQuery || undefined,
          limitCount: 500
        }),
        getMediaClonedVoices({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          isPaid: paidFilter === 'all' ? undefined : paidFilter === 'paid',
          searchQuery: searchQuery || undefined,
          limitCount: 500
        })
      ]);
      
      setVoices(voicesData);
      setCreatedVoices(createdVoicesData);
      setMediaClonedVoices(mediaVoicesData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading voice data:', error);
      toast.error('Failed to load voice cloning data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, relationshipFilter, paidFilter, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Retry single voice processing
  const handleRetry = async (voice: VoiceClone) => {
    const key = `${voice.userId}_${voice.id}`;
    setProcessingIds(prev => new Set([...prev, key]));
    
    try {
      const result = await retryVoiceProcessing(voice.userId, voice.id, adminId);
      if (result.success) {
        toast.success(`Processing started for "${voice.name}"`);
      } else {
        toast.error(result.message);
      }
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Retry failed');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Delete voice
  const handleDelete = async (voice: VoiceClone) => {
    if (!confirm(`Delete voice "${voice.name}" for user ${voice.userDisplayName}? This will remove the voice clone and all associated data.`)) {
      return;
    }

    const key = `${voice.userId}_${voice.id}`;
    setProcessingIds(prev => new Set([...prev, key]));

    try {
      const result = await deleteVoice(voice.userId, voice.id, adminId, 'Admin deletion');
      if (result.success) {
        toast.success(`Voice "${voice.name}" deleted`);
      } else {
        toast.error(result.message);
      }
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Delete failed');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleDeleteCreatedVoice = async (voice: CreatedVoiceProfile) => {
    const soldCount = voice.downloadCount || 0;
    const soldWarning = soldCount > 0
      ? ` This voice already has ${soldCount} marketplace download${soldCount === 1 ? '' : 's'}. Existing buyers will keep their private copies.`
      : '';
    if (!confirm(`Delete created voice "${voice.name}" for user ${voice.userDisplayName || voice.userId}? This will remove the created voice profile and any published listing.${soldWarning}`)) {
      return;
    }

    const key = `created_${voice.userId}_${voice.id}`;
    setProcessingIds(prev => new Set([...prev, key]));

    try {
      const result = await deleteCreatedVoice(voice.userId, voice.id, adminId, 'Admin deletion');
      if (result.success) {
        toast.success(`Created voice "${voice.name}" deleted`);
      } else {
        toast.error(result.message);
      }
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Delete failed');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleUnpublishCreatedVoice = async (voice: CreatedVoiceProfile) => {
    if (!confirm(`Remove "${voice.name}" from the marketplace? Existing buyers keep their copy, but new users will not be able to download it.`)) {
      return;
    }

    const key = `created_unpublish_${voice.userId}_${voice.id}`;
    setProcessingIds(prev => new Set([...prev, key]));

    try {
      const result = await unpublishCreatedVoice(voice.userId, voice.id, adminId, 'Admin unpublish');
      if (result.success) {
        toast.success(`Created voice "${voice.name}" removed from marketplace`);
      } else {
        toast.error(result.message);
      }
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Unpublish failed');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleDeleteMediaClonedVoice = async (voice: MediaClonedVoiceProfile) => {
    if (!confirm(`Delete media clone "${voice.name}" for user ${voice.userDisplayName || voice.userId}? This removes the draft/finalized clone and associated private assets.`)) {
      return;
    }

    const key = `media_${voice.userId}_${voice.id}`;
    setProcessingIds(prev => new Set([...prev, key]));

    try {
      const result = await deleteMediaClonedVoice(voice.userId, voice.id, adminId, 'Admin deletion');
      if (result.success) {
        toast.success(`Media clone "${voice.name}" deleted`);
      } else {
        toast.error(result.message);
      }
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Delete failed');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Bulk retry all failed
  const handleBulkRetry = async () => {
    if (!confirm(`Retry processing for all ${stats?.failedCount || 0} failed voices?`)) {
      return;
    }

    setBulkRetrying(true);
    try {
      const result = await bulkRetryFailedVoices(adminId);
      toast.success(`Bulk retry complete: ${result.succeeded} succeeded, ${result.failed} failed`);
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Bulk retry failed');
    } finally {
      setBulkRetrying(false);
    }
  };

  // Format timestamp
  const formatDate = (ts: any): string => {
    if (!ts) return 'Never';
    const date = ts instanceof Timestamp ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status: VoiceRecordingStatus) => {
    const config: Record<VoiceRecordingStatus, { color: string; icon: any; label: string }> = {
      PENDING: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Pending' },
      RECORDING: { color: 'bg-blue-100 text-blue-700', icon: Mic, label: 'Recording' },
      PROCESSING: { color: 'bg-yellow-100 text-yellow-700', icon: Loader2, label: 'Processing' },
      READY: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Ready' },
      FAILED: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Failed' }
    };
    const { color, icon: Icon, label } = config[status] || config.PENDING;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className={`h-3 w-3 ${status === 'PROCESSING' ? 'animate-spin' : ''}`} />
        {label}
      </Badge>
    );
  };

  const getCreatedVoiceStatusBadge = (status: string) => {
    const normalizedStatus = status || 'DESIGNING';
    const isSpinning = normalizedStatus === 'GENERATING_PREVIEW' || normalizedStatus === 'REGENERATING' || normalizedStatus === 'FINALIZING';
    const config: Record<string, { color: string; icon: any; label: string }> = {
      DESIGNING: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Designing' },
      GENERATING_PREVIEW: { color: 'bg-blue-100 text-blue-700', icon: Loader2, label: 'Generating' },
      PREVIEW_READY: { color: 'bg-cyan-100 text-cyan-700', icon: CheckCircle, label: 'Preview Ready' },
      REGENERATING: { color: 'bg-indigo-100 text-indigo-700', icon: Loader2, label: 'Regenerating' },
      FINALIZING: { color: 'bg-violet-100 text-violet-700', icon: Loader2, label: 'Finalizing' },
      READY: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Ready' },
      FAILED: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Failed' }
    };
    const { color, icon: Icon, label } = config[normalizedStatus] || config.DESIGNING;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className={`h-3 w-3 ${isSpinning ? 'animate-spin' : ''}`} />
        {label}
      </Badge>
    );
  };

  // Get quality score color
  const getQualityColor = (score?: number): string => {
    if (score === undefined) return 'text-gray-400';
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const totalManagedVoices = voices.length + createdVoices.length + mediaClonedVoices.length;
  const readyManagedVoices =
    voices.filter(v => v.recordingStatus === 'READY').length +
    createdVoices.filter(v => v.status === 'READY').length +
    mediaClonedVoices.filter(v => v.status === 'READY').length;
  const processingManagedVoices =
    voices.filter(v => v.recordingStatus === 'PROCESSING').length +
    createdVoices.filter(v => ['GENERATING_PREVIEW', 'REGENERATING', 'FINALIZING'].includes(v.status)).length +
    mediaClonedVoices.filter(v => ['SOURCE_UPLOADED', 'EXTRACTING', 'ANALYZING', 'REGENERATING', 'FINALIZING'].includes(v.status)).length;
  const failedManagedVoices =
    voices.filter(v => v.recordingStatus === 'FAILED').length +
    createdVoices.filter(v => v.status === 'FAILED').length +
    mediaClonedVoices.filter(v => ['FAILED', 'ANALYSIS_FAILED'].includes(v.status)).length;
  const paidManagedVoices =
    voices.filter(v => v.isPaid).length +
    createdVoices.filter(v => v.isPaid).length +
    mediaClonedVoices.filter(v => v.isPaid).length;
  const allQualityVoices = [
    ...voices.filter(v => typeof v.qualityScore === 'number' && v.qualityScore > 0),
    ...createdVoices.filter(v => typeof v.qualityScore === 'number' && v.qualityScore > 0),
    ...mediaClonedVoices.filter(v => typeof v.qualityScore === 'number' && v.qualityScore > 0)
  ];
  const averageQuality = allQualityVoices.length > 0
    ? allQualityVoices.reduce((sum, voice) => sum + (voice.qualityScore || 0), 0) / allQualityVoices.length
    : 0;

  if (loading && voices.length === 0) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Voice Cloning</h2>
            <p className="text-gray-500">Manage loved ones, created voices, and media clones across all NavigatorX users</p>
          </div>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Voices</p>
                  <p className="text-2xl font-bold">{totalManagedVoices}</p>
                </div>
                <Mic className="h-6 w-6 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className={readyManagedVoices > 0 ? 'border-green-200' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Ready</p>
                  <p className="text-2xl font-bold text-green-600">{readyManagedVoices}</p>
                </div>
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className={processingManagedVoices > 0 ? 'border-yellow-200' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Processing</p>
                  <p className="text-2xl font-bold text-yellow-600">{processingManagedVoices}</p>
                </div>
                <Loader2 className={`h-6 w-6 text-yellow-400 ${processingManagedVoices > 0 ? 'animate-spin' : ''}`} />
              </div>
            </CardContent>
          </Card>

          <Card className={failedManagedVoices > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{failedManagedVoices}</p>
                </div>
                <AlertTriangle className="h-6 w-6 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Paid</p>
                  <p className="text-2xl font-bold">{paidManagedVoices}</p>
                </div>
                <DollarSign className="h-6 w-6 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Avg Quality</p>
                  <p className={`text-2xl font-bold ${getQualityColor(averageQuality)}`}>
                    {averageQuality > 0 ? `${(averageQuality * 100).toFixed(0)}%` : '-'}
                  </p>
                </div>
                <Star className="h-6 w-6 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Actions */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, user, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="all">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="RECORDING">Recording</option>
              <option value="PROCESSING">Processing</option>
              <option value="READY">Ready</option>
              <option value="FAILED">Failed</option>
            </select>

            {/* Relationship Filter */}
            <select
              value={relationshipFilter}
              onChange={(e) => setRelationshipFilter(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="all">All Relationships</option>
              {VOICE_RELATIONSHIPS.map(rel => (
                <option key={rel} value={rel}>{rel}</option>
              ))}
            </select>

            {/* Paid Filter */}
            <select
              value={paidFilter}
              onChange={(e) => setPaidFilter(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="all">All Payment</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>

            {/* Bulk Retry Button */}
            {stats && stats.failedCount > 0 && (
              <Button
                variant="outline"
                onClick={handleBulkRetry}
                disabled={bulkRetrying}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                {bulkRetrying ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Retry All Failed ({stats.failedCount})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Voices List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Voice Clones ({voices.length})</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {voices.length === 0 ? (
            <div className="text-center py-12">
              <Mic className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No voice clones found</p>
              {(statusFilter !== 'all' || relationshipFilter !== 'all' || paidFilter !== 'all' || searchQuery) && (
                <Button variant="link" onClick={() => {
                  setStatusFilter('all');
                  setRelationshipFilter('all');
                  setPaidFilter('all');
                  setSearchQuery('');
                }}>
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {voices.map(voice => {
                const isExpanded = expandedVoice === `${voice.userId}_${voice.id}`;
                const isProcessing = processingIds.has(`${voice.userId}_${voice.id}`);
                
                return (
                  <div
                    key={`${voice.userId}_${voice.id}`}
                    className={`border rounded-lg transition-colors ${
                      voice.recordingStatus === 'FAILED' ? 'border-red-200 bg-red-50' :
                      voice.recordingStatus === 'READY' ? 'border-green-200' : ''
                    }`}
                  >
                    {/* Main Row */}
                    <div className="p-4 flex items-center gap-4">
                      {/* Expand Toggle */}
                      <button
                        onClick={() => setExpandedVoice(isExpanded ? null : `${voice.userId}_${voice.id}`)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </button>

                      {/* Voice Icon */}
                      <div className={`p-2 rounded-full ${
                        voice.isActive ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <Volume2 className={`h-5 w-5 ${voice.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>

                      {/* Voice Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{voice.name}</span>
                          {voice.nickname && (
                            <span className="text-sm text-gray-500">"{voice.nickname}"</span>
                          )}
                          <Badge variant="outline" className="text-xs">{voice.relationship}</Badge>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                          <Users className="h-3 w-3" />
                          <span className="truncate">{voice.userDisplayName || voice.userId}</span>
                          {voice.userEmail && (
                            <span className="text-gray-400 truncate">({voice.userEmail})</span>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="flex items-center gap-2">
                        {getStatusBadge(voice.recordingStatus)}
                      </div>

                      {/* Payment Status */}
                      <div>
                        {voice.isPaid ? (
                          <Badge className="bg-green-100 text-green-700">Paid</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">Unpaid</Badge>
                        )}
                      </div>

                      {/* Quality Score */}
                      {voice.qualityScore !== undefined && (
                        <div className={`text-sm font-medium ${getQualityColor(voice.qualityScore)}`}>
                          {(voice.qualityScore * 100).toFixed(0)}%
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedVoice(voice)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>

                        {voice.recordingStatus === 'FAILED' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRetry(voice)}
                            disabled={isProcessing}
                            title="Retry Processing"
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            {isProcessing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(voice)}
                          disabled={isProcessing}
                          title="Delete Voice"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0 border-t bg-gray-50">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 text-sm">
                          <div>
                            <p className="text-gray-500">Voice ID</p>
                            <code className="text-xs bg-gray-200 px-1 rounded">{voice.id}</code>
                          </div>
                          <div>
                            <p className="text-gray-500">User ID</p>
                            <code className="text-xs bg-gray-200 px-1 rounded">{voice.userId}</code>
                          </div>
                          <div>
                            <p className="text-gray-500">Created</p>
                            <p>{formatDate(voice.createdAt)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Recorded</p>
                            <p>{formatDate(voice.recordedAt)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Processed</p>
                            <p>{formatDate(voice.processedAt)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Wake Word</p>
                            <p>{voice.wakeWord || 'Default'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Voice ID</p>
                            <code className="text-xs bg-gray-200 px-1 rounded">
                              {voice.voiceId || voice.elevenLabsVoiceId || 'N/A'}
                            </code>
                          </div>
                          <div>
                            <p className="text-gray-500">Cached Phrases</p>
                            <p>{voice.cachedPhrases ? Object.keys(voice.cachedPhrases).length : 0}</p>
                          </div>
                        </div>

                        {/* Error Display */}
                        {voice.lastError && (
                          <div className="mt-4 p-3 bg-red-100 border border-red-200 rounded-lg">
                            <p className="text-sm font-medium text-red-800">Last Error:</p>
                            <p className="text-sm text-red-700 mt-1">{voice.lastError}</p>
                            {voice.errorCount && voice.errorCount > 1 && (
                              <p className="text-xs text-red-500 mt-1">
                                Failed {voice.errorCount} times
                              </p>
                            )}
                          </div>
                        )}

                        {/* Voice Settings */}
                        <div className="mt-4 flex gap-4">
                          <div className="text-sm">
                            <span className="text-gray-500">Volume:</span>{' '}
                            <span className="font-medium">{(voice.volume * 100).toFixed(0)}%</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-500">Pitch:</span>{' '}
                            <span className="font-medium">{(voice.pitch * 100).toFixed(0)}%</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-500">Speed:</span>{' '}
                            <span className="font-medium">{(voice.speed * 100).toFixed(0)}%</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-500">Active:</span>{' '}
                            <span className={`font-medium ${voice.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                              {voice.isActive ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Media Clones ({mediaClonedVoices.length})</CardTitle>
              <CardDescription>
                YouTube and uploaded-audio clones stored privately under each user until explicitly saved or deleted.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {mediaClonedVoices.length === 0 ? (
            <div className="text-center py-12">
              <Zap className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No media-cloned voices found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {mediaClonedVoices.map((voice) => {
                const processingKey = `media_${voice.userId}_${voice.id}`;
                const isProcessing = processingIds.has(processingKey);

                return (
                  <div
                    key={processingKey}
                    className={`border rounded-lg transition-colors ${
                      voice.status === 'FAILED' || voice.status === 'ANALYSIS_FAILED' ? 'border-red-200 bg-red-50' :
                      voice.status === 'READY' ? 'border-green-200' : ''
                    }`}
                  >
                    <div className="p-4 flex items-center gap-4">
                      <div className={`p-2 rounded-full ${voice.isPaid ? 'bg-sky-100' : 'bg-gray-100'}`}>
                        <Zap className={`h-5 w-5 ${voice.isPaid ? 'text-sky-600' : 'text-gray-400'}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{voice.name}</span>
                          {getCreatedVoiceStatusBadge(voice.status)}
                          {voice.isPaid ? (
                            <Badge className="bg-green-100 text-green-700">Paid</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">Unpaid</Badge>
                          )}
                          <Badge className="bg-sky-100 text-sky-700">{voice.sourceType || 'Unknown Source'}</Badge>
                          {voice.finalizedByAdmin ? (
                            <Badge className="bg-amber-100 text-amber-700">Admin Saved</Badge>
                          ) : null}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                          <Users className="h-3 w-3" />
                          <span className="truncate">{voice.userDisplayName || voice.userId}</span>
                          {voice.userEmail && (
                            <span className="text-gray-400 truncate">({voice.userEmail})</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                          <span>ID: {voice.id}</span>
                          {voice.qualityScore !== undefined && (
                            <>
                              <span>•</span>
                              <span className={getQualityColor(voice.qualityScore)}>
                                {(voice.qualityScore * 100).toFixed(0)}% quality
                              </span>
                            </>
                          )}
                          {voice.usableSpeechSeconds !== undefined && (
                            <>
                              <span>•</span>
                              <span>{voice.usableSpeechSeconds.toFixed(0)} sec speech</span>
                            </>
                          )}
                          <span>•</span>
                          <span>Updated {formatDate(voice.updatedAt || voice.createdAt)}</span>
                        </div>
                        {voice.failureMessage && (
                          <p className="text-sm text-red-600 mt-2 line-clamp-2 max-w-3xl">{voice.failureMessage}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteMediaClonedVoice(voice)}
                          disabled={isProcessing}
                          title="Delete Media Clone"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          {isProcessing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Created Voices ({createdVoices.length})</CardTitle>
              <CardDescription>
                Synthetic voices created from text prompts, including unpaid drafts and published entries.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {createdVoices.length === 0 ? (
            <div className="text-center py-12">
              <Volume2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No created voices found</p>
              {(paidFilter !== 'all' || searchQuery) && (
                <Button variant="link" onClick={() => {
                  setPaidFilter('all');
                  setSearchQuery('');
                }}>
                  Clear created voice filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {createdVoices.map((voice) => {
                const processingKey = `created_${voice.userId}_${voice.id}`;
                const isProcessing = processingIds.has(processingKey) || processingIds.has(`created_unpublish_${voice.userId}_${voice.id}`);

                return (
                  <div
                    key={processingKey}
                    className={`border rounded-lg transition-colors ${
                      voice.status === 'FAILED' ? 'border-red-200 bg-red-50' :
                      voice.status === 'READY' ? 'border-green-200' : ''
                    }`}
                  >
                    <div className="p-4 flex items-center gap-4">
                      <div className={`p-2 rounded-full ${voice.isPaid ? 'bg-violet-100' : 'bg-gray-100'}`}>
                        <Volume2 className={`h-5 w-5 ${voice.isPaid ? 'text-violet-600' : 'text-gray-400'}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{voice.name}</span>
                          {getCreatedVoiceStatusBadge(voice.status)}
                          {voice.isPaid ? (
                            <Badge className="bg-green-100 text-green-700">Paid</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">Unpaid</Badge>
                          )}
                          {voice.isPublic ? (
                            <Badge className="bg-blue-100 text-blue-700">Public</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-600">Private</Badge>
                          )}
                          {(voice.downloadCount || 0) > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700">
                              {(voice.downloadCount || 0)} downloads
                            </Badge>
                          ) : null}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                          <Users className="h-3 w-3" />
                          <span className="truncate">{voice.userDisplayName || voice.userId}</span>
                          {voice.userEmail && (
                            <span className="text-gray-400 truncate">({voice.userEmail})</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                          <span>ID: {voice.id}</span>
                          <span>•</span>
                          <span>{voice.gender || 'Unknown gender'}</span>
                          <span>•</span>
                          <span>{voice.age || 'Unknown age'}</span>
                          {voice.qualityScore !== undefined && (
                            <>
                              <span>•</span>
                              <span className={getQualityColor(voice.qualityScore)}>
                                {(voice.qualityScore * 100).toFixed(0)}% quality
                              </span>
                            </>
                          )}
                          <span>•</span>
                          <span>Updated {formatDate(voice.updatedAt || voice.createdAt)}</span>
                        </div>
                        {voice.description && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2 max-w-3xl">{voice.description}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {voice.isPublic ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnpublishCreatedVoice(voice)}
                            disabled={isProcessing}
                            title="Remove From Marketplace"
                            className="text-amber-700 border-amber-300 hover:bg-amber-50"
                          >
                            {isProcessing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteCreatedVoice(voice)}
                          disabled={isProcessing}
                          title="Delete Created Voice"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          {isProcessing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Voice Detail Modal */}
      {selectedVoice && (
        <VoiceDetailModal
          voice={selectedVoice}
          onClose={() => setSelectedVoice(null)}
          onUpdate={loadData}
        />
      )}
    </div>
  );
}
